import { useNavigate, useParams } from "@tanstack/react-router"
import React, { useEffect, useRef, useState } from "react"

import { trpc } from "../../lib/trpc"

interface Message {
  id: string
  content: string
  direction: "inbound" | "outbound"
  senderType: "bot" | "human" | "contact"
  templateUsed?: string
  sentAt: string
  deliveryStatus: "pending" | "sent" | "delivered" | "failed"
}

interface Conversation {
  id: string
  status: "active" | "paused" | "completed" | "human_takeover"
  platform: string
  priority: number
  humanTakeover: boolean
  humanTakeoverReason?: string
  contact?: {
    id: string
    name?: string
    email?: string
    phone?: string
    company?: string
  }
  campaign?: {
    id: string
    name: string
    stages?: {
      id: string
      name: string
      orderIndex: number
    }[]
  }
  currentStage?: {
    id: string
    name: string
  }
  messages?: Message[]
  aiContext: any
  extractedData?: any
  lastActivityAt: string
  createdAt: string
}

const StatusBadge: React.FC<{ status: string; humanTakeover?: boolean }> = ({ status, humanTakeover }) => {
  if (humanTakeover) {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-300">
        👨‍💻 Human Control
      </span>
    )
  }

  const colors = {
    active: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-300",
    paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-300",
    completed: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300",
    human_takeover: "bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-300",
  }

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status as keyof typeof colors] || colors.active}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ")}
    </span>
  )
}

const PlatformIcon: React.FC<{ platform: string }> = ({ platform }) => {
  const icons = {
    email: "📧",
    sms: "💬",
    whatsapp: "💚",
    messenger: "💙",
  }

  return <span className="text-lg">{icons[platform as keyof typeof icons] || "💬"}</span>
}

const ConversationDetail: React.FC = () => {
  const params = useParams({ from: "/crm/inbox/$id" })
  const navigate = useNavigate()
  const [newMessage, setNewMessage] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [showStageModal, setShowStageModal] = useState(false)
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [noteText, setNoteText] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const conversationId = params.id

  // Query conversation details
  const {
    data: conversation,
    isLoading,
    refetch,
  } = trpc.crm.conversations.getById.useQuery({ id: conversationId }, { enabled: !!conversationId })

  // Send message mutation
  const sendMessageMutation = trpc.crm.conversations.sendMessage.useMutation({
    onSuccess: () => {
      setNewMessage("")
      void refetch()
    },
  })

  // Update conversation status mutation
  const updateStatusMutation = trpc.crm.conversations.updateStatus.useMutation({
    onSuccess: () => {
      void refetch()
    },
  })

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [conversation?.messages])

  const handleSendMessage = () => {
    if (!newMessage.trim()) return

    setIsTyping(true)
    sendMessageMutation.mutate({
      conversationId,
      content: newMessage.trim(),
      direction: "outbound",
    })
    setIsTyping(false)
  }

  const handleStatusChange = (newStatus: "active" | "paused" | "completed" | "human_takeover", reason?: string) => {
    updateStatusMutation.mutate({
      conversationId,
      status: newStatus,
      reason,
    })
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-foregroundx ">
        <div className="text-gray-500">Loading conversation...</div>
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center bg-foregroundx ">
        <div className="text-center">
          <div className="text-gray-500 mb-4">Conversation not found</div>
          <button
            onClick={() => navigate({ to: "/crm/inbox" })}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Inbox
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-foregroundx ">
      {/* Header */}
      <div className="bg-background  border-b border-border dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate({ to: "/crm/inbox" })}
              className=" hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ← Back
            </button>

            <PlatformIcon platform={conversation.platform} />

            <div>
              <h1 className="text-xl font-semibold  dark:text-white">
                {(conversation.contact as any)?.name ||
                  (conversation.contact as any)?.email ||
                  (conversation.contact as any)?.phone ||
                  "Unknown Contact"}
              </h1>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {(conversation.campaign as any)?.name} • {(conversation.currentStage as any)?.name || "No stage"}
              </div>
            </div>

            <StatusBadge status={conversation.status} humanTakeover={conversation.humanTakeover} />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate({ to: `/crm/contacts/${(conversation.contact as any)?.id}` })}
              className=" hover:text-blue-800 text-sm px-3 py-1 border border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              View Contact
            </button>

            {!conversation.humanTakeover && conversation.status === "active" && (
              <button
                onClick={() => handleStatusChange("human_takeover", "Manual takeover from conversation detail")}
                className="text-orange-600 hover:text-orange-800 text-sm px-3 py-1 border border-orange-600 rounded hover:bg-orange-50 dark:hover:bg-orange-900/20"
              >
                Take Over
              </button>
            )}

            {conversation.humanTakeover && (
              <button
                onClick={() => handleStatusChange("active", "Released back to bot")}
                className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-600 rounded hover:bg-green-50 dark:hover:bg-green-900/20"
              >
                Release to Bot
              </button>
            )}

            {conversation.status === "active" && (
              <button
                onClick={() => handleStatusChange("paused", "Paused from conversation detail")}
                className="text-yellow-600 hover:text-yellow-800 text-sm px-3 py-1 border border-yellow-600 rounded hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
              >
                Pause
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Conversation Content */}
      <div className="flex-1 flex">
        {/* Messages */}
        <div className="flex-1 flex flex-col">
          {/* Messages List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {conversation.messages && conversation.messages.length > 0 ? (
              conversation.messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.direction === "outbound" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.direction === "outbound"
                        ? "bg-blue-600 text-white"
                        : "bg-background   dark:text-white border border-border dark:border-gray-700"
                    }`}
                  >
                    <div className="text-sm">{message.content}</div>
                    <div
                      className={`text-xs mt-1 ${message.direction === "outbound" ? "text-blue-100" : "text-gray-500"}`}
                    >
                      {message.senderType === "bot" && "🤖 "}
                      {message.senderType === "human" && "👨‍💻 "}
                      {new Date(message.sentAt).toLocaleTimeString()}
                      {message.templateUsed && <span className="ml-2">• Template: {message.templateUsed}</span>}
                      {message.deliveryStatus === "failed" && <span className="text-red-400 ml-2">• Failed</span>}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <div className="text-4xl mb-2">💬</div>
                  <div>No messages yet</div>
                  <div className="text-sm">Start the conversation below</div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          {(conversation.humanTakeover || conversation.status === "active") && (
            <div className="border-t border-border dark:border-gray-700 p-4 bg-background ">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  placeholder="Type your message..."
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                  disabled={sendMessageMutation.isLoading || isTyping}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendMessageMutation.isLoading || isTyping}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendMessageMutation.isLoading ? "Sending..." : "Send"}
                </button>
              </div>

              {conversation.humanTakeover && (
                <div className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                  💡 You&apos;re in control. Messages will be sent manually.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="w-80 bg-background  border-l border-border dark:border-gray-700 p-4 overflow-y-auto">
          {/* Contact Info */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold  dark:text-white mb-3">Contact</h3>
            <div className="space-y-2">
              <div>
                <div className="text-sm font-medium text-primary dark:text-gray-300">Name</div>
                <div className=" dark:text-white">{(conversation.contact as any)?.name || "Not provided"}</div>
              </div>

              {(conversation.contact as any)?.email && (
                <div>
                  <div className="text-sm font-medium text-primary dark:text-gray-300">Email</div>
                  <div className=" dark:text-white">{(conversation.contact as any)?.email}</div>
                </div>
              )}

              {(conversation.contact as any)?.phone && (
                <div>
                  <div className="text-sm font-medium text-primary dark:text-gray-300">Phone</div>
                  <div className=" dark:text-white">{(conversation.contact as any)?.phone}</div>
                </div>
              )}

              {(conversation.contact as any)?.company && (
                <div>
                  <div className="text-sm font-medium text-primary dark:text-gray-300">Company</div>
                  <div className=" dark:text-white">{(conversation.contact as any)?.company}</div>
                </div>
              )}
            </div>
          </div>

          {/* AI Context */}
          {conversation.aiContext && Object.keys(conversation.aiContext).length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold  dark:text-white mb-3">AI Context</h3>
              <div className="bg-foregroundx dark:bg-gray-700 rounded-lg p-3">
                <pre className="text-xs text-primary dark:text-gray-300 whitespace-pre-wrap">
                  {JSON.stringify(conversation.aiContext, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Extracted Data */}
          {conversation.extractedData && Object.keys(conversation.extractedData).length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold  dark:text-white mb-3">Extracted Data</h3>
              <div className="bg-foregroundx dark:bg-gray-700 rounded-lg p-3">
                <pre className="text-xs text-primary dark:text-gray-300 whitespace-pre-wrap">
                  {JSON.stringify(conversation.extractedData, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Conversation Stats */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold  dark:text-white mb-3">Stats</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className=" dark:text-gray-400">Messages</span>
                <span className=" dark:text-white">{conversation.messages?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className=" dark:text-gray-400">Platform</span>
                <span className=" dark:text-white">{conversation.platform}</span>
              </div>
              <div className="flex justify-between">
                <span className=" dark:text-gray-400">Priority</span>
                <span className=" dark:text-white">{conversation.priority}/10</span>
              </div>
              <div className="flex justify-between">
                <span className=" dark:text-gray-400">Started</span>
                <span className=" dark:text-white">{new Date(conversation.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className=" dark:text-gray-400">Last Activity</span>
                <span className=" dark:text-white">{new Date(conversation.lastActivityAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Human Takeover Info */}
          {conversation.humanTakeover && conversation.humanTakeoverReason && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold  dark:text-white mb-3">Takeover Reason</h3>
              <div className="text-sm text-primary dark:text-gray-300 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                {conversation.humanTakeoverReason}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold  dark:text-white mb-3">Quick Actions</h3>

            <button
              onClick={() => navigate({ to: `/crm/campaigns/${(conversation.campaign as any)?.id}` })}
              className="w-full text-left p-2 text-sm  hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
            >
              View Campaign
            </button>

            <button
              onClick={() => setShowStageModal(true)}
              className="w-full text-left p-2 text-sm text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded"
            >
              Change Stage
            </button>

            <button
              onClick={() => setShowNoteModal(true)}
              className="w-full text-left p-2 text-sm   dark:hover:bg-gray-700 rounded"
            >
              Add Note
            </button>

            <button
              onClick={() => handleStatusChange("completed", "Marked as completed from conversation detail")}
              className="w-full text-left p-2 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
            >
              Mark Complete
            </button>
          </div>
        </div>
      </div>

      {/* Stage Change Modal */}
      {showStageModal &&
        conversation?.campaign &&
        !Array.isArray(conversation.campaign) &&
        conversation.campaign.stages && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background  rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold  dark:text-white mb-4">Change Stage</h2>
              <div className="space-y-3">
                <div className="text-sm  dark:text-gray-400 mb-4">
                  Current:{" "}
                  {(conversation && !Array.isArray(conversation.currentStage) && conversation.currentStage?.name) ||
                    "No stage"}
                </div>
                {!Array.isArray(conversation.campaign) &&
                  conversation.campaign.stages?.map((stage) => (
                    <button
                      key={stage.id}
                      onClick={() => {
                        // For now, just show a success message
                        alert(`Stage would be changed to: ${stage.name}`)
                        setShowStageModal(false)
                      }}
                      className={`w-full text-left p-3 border rounded-lg transition-colors ${
                        stage.id ===
                        (conversation && !Array.isArray(conversation.currentStage) && conversation.currentStage?.id)
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                          : "border-border dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                      }`}
                    >
                      <div className="font-medium  dark:text-white">{stage.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Stage {stage.orderIndex + 1}</div>
                    </button>
                  ))}
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowStageModal(false)}
                  className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background  rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold  dark:text-white mb-4">Add Note</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-2">Note</label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note about this conversation..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowNoteModal(false)
                  setNoteText("")
                }}
                className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (noteText.trim()) {
                    // For now, just show a success message
                    alert(`Note added: ${noteText}`)
                    setShowNoteModal(false)
                    setNoteText("")
                  }
                }}
                disabled={!noteText.trim()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConversationDetail
